import { z } from 'zod'

import { appType } from 'src/utils/appType'

const logoutMessageSchema = z.object({ type: z.literal('logout') })

// One channel per tab for sending and receiving: a BroadcastChannel never
// receives its own messages, so a tab never hears its own logout.
let channel: BroadcastChannel | null | undefined

const getChannel = () => {
  if (channel === undefined) {
    // jsdom (Jest) has no BroadcastChannel; there, tabs just don't sync instead of throwing.
    channel =
      typeof BroadcastChannel === 'undefined'
        ? null
        : new BroadcastChannel(`develo-logout-${appType}`)
  }
  return channel
}

/**
 * Logs out, then tells this app's other tabs to re-check their session, so they
 * drop to the login page if it's gone. Broadcasts after the server logout, so
 * they find the session cookie gone. If the logout request fails, this tab stays
 * logged in and so do the others.
 */
export const logOutAndSyncTabs = async (logOut: () => Promise<unknown>) => {
  await logOut()
  getChannel()?.postMessage({ type: 'logout' })
}

/** Calls `onLogout` when another tab of this app logs out. Returns an unsubscribe. */
export const subscribeToLogout = (onLogout: () => void) => {
  const logoutChannel = getChannel()
  if (!logoutChannel) return () => undefined

  const onMessage = (event: MessageEvent) => {
    if (logoutMessageSchema.safeParse(event.data).success) onLogout()
  }
  logoutChannel.addEventListener('message', onMessage)
  return () => logoutChannel.removeEventListener('message', onMessage)
}
